// Mirrors backend pricing.py: cents rounded half-up, line totals from exact sq ft.
import { componentCost } from "@/lib/components";
import { defaultColor } from "@/lib/defaults";

export const r2 = (x) => {
  const n = Number(Number(x).toPrecision(15));
  return Number(Math.round(Number(`${n}e2`)) + "e-2");
};

export const toNum = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const isMapped = (it) => !!it.type_id && it.type_name !== "Unmapped";

export function priceItem(it, multiplier) {
  const w = toNum(it.width_in);
  const h = toNum(it.height_in);
  const qty = toNum(it.qty) ?? 0;
  const cps = (toNum(it.cost_per_sqft) ?? 0) + (toNum(it.glass_cost_per_sqft) ?? 0) + (it.arch ? toNum(it.arch_cost_per_sqft) ?? 0 : 0)
    + (it.grids ? toNum(it.grid_cost_per_sqft) ?? 0 : 0);
  const sizeOk = w > 0 && h > 0;
  const mapped = isMapped(it);
  const perItem = it.pricing_mode === "item";
  let sqft = 0, cost = 0, suggested = 0;
  if (sizeOk) sqft = r2((w * h) / 144);
  let warn = "", parts = null;
  if (it.pricing_mode === "component") {
    if (it.design?.root && it.component?.design_unit != null) {
      warn = it.component.warn || "";
      parts = it.component.breakdown || [];
      cost = sizeOk ? r2(it.component.design_unit * qty) : 0;
      suggested = r2(cost * (toNum(multiplier) ?? 0));
    } else if (it.component?.line && !it.design?.root) {
      const res = componentCost(it.component.line, it);
      warn = res.warn;
      parts = res.parts.map(([label, amount]) => ({ label, amount: r2(amount) }));
      cost = sizeOk ? r2(res.unit * qty) : 0;
      suggested = r2(cost * (toNum(multiplier) ?? 0));
    } else {
      suggested = sizeOk ? r2((toNum(it.component_unit_sell) ?? 0) * qty) : 0;
    }
  } else if (mapped && perItem) {
    const opts = cps - (toNum(it.cost_per_sqft) ?? 0);
    cost = r2((toNum(it.item_cost) ?? 0) * qty + (sizeOk ? (w * h * opts * qty) / 144 : 0));
    suggested = r2(cost * (toNum(multiplier) ?? 0));
  } else if (sizeOk && mapped) {
    cost = r2((w * h * cps * qty) / 144);
    suggested = r2(cost * (toNum(multiplier) ?? 0));
  }
  const override = toNum(it.price_override);
  const edited = override !== null;
  const final = edited ? r2(override) : suggested;
  const unit = qty ? r2(final / qty) : final;
  return { sqft, cost, suggested, final, unit, edited, sizeOk, mapped, perItem, warn, parts, missingW: !(w > 0), missingH: !(h > 0) };
}

export function totals(rows, charges = {}) {
  const sum = (k) => r2(rows.reduce((a, r) => a + r.calc[k], 0));
  const subtotal = sum("final");
  const upgrade = r2(toNum(charges.upgrade_amount) ?? 0);
  const shipping = r2(toNum(charges.shipping) ?? 0);
  const taxable = r2(subtotal + upgrade);
  const tax = r2((Math.max(taxable, 0) * (toNum(charges.tax_rate) ?? 0)) / 100);
  return {
    cost: sum("cost"), suggested: sum("suggested"), final: subtotal, subtotal, upgrade, shipping, tax,
    project_total: r2(taxable + shipping + tax),
  };
}

export const SPEC_FIELDS = [
  ["product_name", "Product name"], ["model", "Model"], ["frame", "Frame"],
  ["ext_color", "Ext color"], ["int_color", "Int color"], ["glass_details", "Glass notes"], ["hardware", "Hardware"],
];
const emptySpecs = () => Object.fromEntries(SPEC_FIELDS.map(([k]) => [k, ""]));

export const GLASS_GROUPS = [
  ["glazing", "Glazing"], ["glass_types", "Glass type"], ["gas", "Gas fill"], ["thickness", "Thickness / spacer"],
];
export const ARCH_STYLES = [["half_round", "Half Round"], ["segment", "Segment (eyebrow)"], ["gothic", "Gothic"]];
export const GLASS_FIELDS = ["glazing", "glass_types", "gas", "thickness", "u_value", "shgc"];
export const GRID_FIELDS = ["grids", "grid_pattern", "grid_rows", "grid_cols", "grid_type", "grid_color"];
export const GRID_PATTERNS = [["colonial", "Colonial"], ["prairie", "Prairie"], ["perimeter", "Perimeter"], ["diamond", "Diamond"]];
export const GRID_TYPES = [["GBG", "Between the Glass (GBG)"], ["SDL", "Simulated Divided Lite (SDL)"], ["TDL", "True Divided Lite (TDL)"]];
export const gridDefaults = () => ({ grids: false, grid_pattern: "colonial", grid_rows: 2, grid_cols: 2, grid_type: "SDL", grid_color: "" });
export const gridSummary = (it) => {
  if (!it.grids) return "";
  const name = (GRID_PATTERNS.find(([v]) => v === it.grid_pattern) || [0, "Colonial"])[1];
  const size = it.grid_pattern === "colonial" ? ` ${it.grid_rows}×${it.grid_cols}` : "";
  return [name + size, it.grid_type, it.grid_color || it.ext_color].filter(Boolean).join(" · ");
};
const emptyGlass = () => ({ glazing: "", glass_types: [], gas: "", thickness: "", u_value: "", shgc: "" });

// Per-sq-ft extras from the company's current settings (snapshotted onto the line).
export function lineExtras(it, company) {
  const opts = company?.glass_options || {};
  const cost = (group, names) => names.reduce((a, n) => a + (toNum((opts[group] || []).find((o) => o.name === n)?.cost) ?? 0), 0);
  const glass = cost("glazing", [it.glazing]) + cost("glass_types", it.glass_types || []) + cost("gas", [it.gas]) + cost("thickness", [it.thickness]);
  return {
    glass_cost_per_sqft: r2(glass * 10000) / 10000, arch_cost_per_sqft: toNum(company?.arch_surcharge) ?? 0,
    grid_cost_per_sqft: toNum(company?.grid_costs?.[it.grid_type || "SDL"]) ?? 0,
  };
}

export const glassSummary = (it) => [it.glazing, (it.glass_types || []).join(", "), it.gas, it.thickness].filter(Boolean).join(" · ");

export const badRating = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return false;
  const n = toNum(v);
  return n === null || n < 0;
};

const clampGrid = (v) => Math.min(12, Math.max(1, Math.round(toNum(v) || 1)));

export const SLIDING_KINDS = ["sliding", "sliding_patio"];
export const CONFIG_ERR = "Use X and O, 2 to 6 panels";
export const isSliding = (it) => SLIDING_KINDS.includes(it.sketch) || (!it.sketch && /slid|glider/i.test(it.type_name || ""));
export const badConfig = (v) => !!(v || "").trim() && !/^[XO]{2,6}$/.test((v || "").replace(/[\s\-/|]/g, "").toUpperCase());
export const cleanConfig = (v) => (v || "").replace(/[\s\-/|]/g, "").toUpperCase();

export const sketchParams = (it, mapped) => ({
  sketch: it.sketch || "", type_name: it.type_name || "", category: it.category || "window", mapped: !!mapped,
  w: toNum(it.width_in) > 0 ? toNum(it.width_in) : undefined, h: toNum(it.height_in) > 0 ? toNum(it.height_in) : undefined,
  arch: !!it.arch || !!it.shape_style, arch_style: it.shape_style || it.arch_style || "half_round",
  grids: !!it.grids, grid_pattern: it.grid_pattern || "colonial", grid_rows: clampGrid(it.grid_rows), grid_cols: clampGrid(it.grid_cols),
  ...(isSliding(it) && !badConfig(it.config) ? { config: cleanConfig(it.config) } : {}),
  ...(it.sketch === "bifold" || it.component?.line?.kind === "bifold" ? { sketch: "bifold", config: it.fold || `${it.panels || 3}L` } : {}),
  ...(it.pricing_mode === "component" && it.layout ? { layout: it.layout, hinge: it.component?.line?.category === "awning" ? "top" : "left" } : {}),
});

const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const money = (v) => fmt.format(Number(v) || 0);

export const newItem = () => ({
  id: Math.random().toString(36).slice(2) + Date.now().toString(36),
  mark: "", category: "window", raw_type: "", type_id: null, type_name: "Unmapped", cost_per_sqft: 0,
  width_in: "", height_in: "", qty: 1, notes: "", confidence: null, price_override: null,
  location: "", rough_opening: "", sketch: "", ...emptySpecs(), ext_color: defaultColor(), int_color: defaultColor(), color_default: true, ...emptyGlass(),
  arch: false, arch_style: "half_round", glass_cost_per_sqft: 0, arch_cost_per_sqft: 0, ...gridDefaults(), grid_cost_per_sqft: 0,
  pricing_mode: "sqft", item_cost: 0, catalog_item_id: null, sku: "", catalog_match: "",
});

export function serializeItem(it) {
  return {
    id: it.id, mark: it.mark || "", category: it.category, raw_type: it.raw_type || "", type_id: it.type_id || null,
    type_name: it.type_name || "Unmapped", cost_per_sqft: toNum(it.cost_per_sqft) ?? 0,
    width_in: toNum(it.width_in), height_in: toNum(it.height_in), qty: Math.max(0, Math.round(toNum(it.qty) ?? 0)),
    notes: it.notes || "", confidence: it.confidence ?? null, price_override: toNum(it.price_override),
    location: it.location || "", rough_opening: it.rough_opening || "", sketch: it.sketch || "",
    ...Object.fromEntries(SPEC_FIELDS.map(([k]) => [k, it[k] || ""])),
    glass: it.glass || "", arch: !!it.arch, arch_style: it.arch_style || "half_round",
    glazing: it.glazing || "", glass_types: it.glass_types || [], gas: it.gas || "", thickness: it.thickness || "",
    u_value: toNum(it.u_value), shgc: toNum(it.shgc),
    glass_cost_per_sqft: toNum(it.glass_cost_per_sqft) ?? 0, arch_cost_per_sqft: toNum(it.arch_cost_per_sqft) ?? 0,
    grids: !!it.grids, grid_pattern: it.grid_pattern || "colonial", grid_type: it.grid_type || "SDL", grid_color: it.grid_color || "",
    grid_rows: Math.min(12, Math.max(1, Math.round(toNum(it.grid_rows) || 1))), grid_cols: Math.min(12, Math.max(1, Math.round(toNum(it.grid_cols) || 1))),
    grid_cost_per_sqft: toNum(it.grid_cost_per_sqft) ?? 0, nominal: !!it.nominal,
    pricing_mode: it.pricing_mode || "sqft", item_cost: toNum(it.item_cost) ?? 0, catalog_item_id: it.catalog_item_id || null,
    sku: it.sku || "", catalog_match: it.catalog_match || "",
    source_file: it.source_file || "", source_file_ids: it.source_file_ids || [], user_edited: !!it.user_edited, review_flag: it.review_flag || "",
    config: isSliding(it) ? cleanConfig(it.config) : "",
    product_line_id: it.product_line_id || null, no_line: !!it.no_line,
    shape_id: it.shape_id || null, shape_name: it.shape_name || "", shape_style: it.shape_style || "",
    row_heights: (it.row_heights || []).map(Number).filter(Boolean), design: it.design?.root ? it.design : {}, design_summary: it.design_summary || "", design_snapshot: it.design?.root ? it.design_snapshot || "" : "", catalog_id: it.catalog_id || null, catalog_code: it.catalog_code || "", catalog_photo: it.catalog_photo || "", match_status: it.match_status || "", match_label: it.match_label || "", match_key: it.match_key || "", their_price: it.their_price ?? null, glass_default: !!it.glass_default, color_default: !!it.color_default,
    series: it.series === "element" ? "element" : "standard", series_prev: it.series_prev || {}, element_pct: toNum(it.element_pct), element_rated: !!it.element_rated,
    screen_id: it.screen_id || null, screen_name: it.screen_name || "", screen_pull: it.screen_pull === "double" ? "double" : "single",
    price_source: it.price_override != null && it.price_override !== "" ? it.price_source || "" : "", check_price: !!it.check_price, layout: it.layout || "", layout_auto: it.layout_auto === false ? false : it.layout_auto ?? null, panel_widths: (it.panel_widths || []).map(Number).filter((x) => x > 0),
    sash_sizes: (it.sash_sizes || []).map((s) => (s || []).map(Number)).filter((s) => s.length === 2 && s[0] > 0 && s[1] > 0),
    panels: Math.max(0, Math.min(8, Math.round(toNum(it.panels) || 0))), fold: /^([1-8][LR]){1,2}$/.test(it.fold || "") ? it.fold : "", smart_lock: !!it.smart_lock,
    track: it.track || "", glass_upgrade: it.glass_upgrade || "", extras: (it.extras || []).filter((e) => Number(e.qty) > 0),
  };
}
